import { message } from "antd";
import React, { useCallback, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";

import { GeneralLayout, SingleLayout } from "layouts";

import {
  setTokenCookie,
  deleteTokenCookie,
  getTokenCookieValue,
  setAuthorizationInDefaultCommonHeaderToApiHeru,
} from "utils/requests";
import { postAuth } from "services/auth.service";
import { routeDigitalStamps, routeHome, routeLogin, routeUploadFiles } from "routes/pages";

import { INIT_STATE_REQUEST, singleLayoutByRoutes } from "constants/commons";

import type { ReactNode } from "react";
import type { LoginParams } from "types/auth";
import { useAuthMfaSecure } from "providers/AuthSecureManage/AuthMfaSecureManage";

export const SessionManageContext = React.createContext<ItfAuthManageContext>({
  login: INIT_STATE_REQUEST,
  submitLogin: (): null => null,
  submitLogout: (): null => null,
});

export function SessionManage({ children = null }: { children?: ReactNode }): JSX.Element {
  const urlLocation = useLocation();
  const urlHistory = useHistory();
  const { secureSignOut, getCurrentSession, getAuthenticatorAssuranceLevel } = useAuthMfaSecure();
  const [login, setLogin] = React.useState(INIT_STATE_REQUEST);
  const [isCheckingSession, setIsCheckingSession] = React.useState(true);

  const canAccessRTCertifications = (userId: number | null): boolean => {
   return true;
  };
  const canAccessRappi = (userId: number | null): boolean => {
   return true;
  }

  const submitLogin = useCallback(async(body: LoginParams) => {
    setLogin({ ...login, loading: true });
    try {
      setTokenCookie({ token: 'access_token', maxAge: 12233 });
      setAuthorizationInDefaultCommonHeaderToApiHeru(`Bearer ${'access_token'}`);
;
    } catch (error) {
      console.log(error);
      message.error("Usuario o contraseña incorrectos");
    }
  }, []);


  const submitLogout = useCallback(async () => {
    try {
      await secureSignOut();
      deleteTokenCookie();
      sessionStorage.removeItem('user_id');
      urlHistory.push(routeLogin());
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Forzar limpieza incluso si hay error
      deleteTokenCookie();
      sessionStorage.removeItem('user_id');
      urlHistory.push(routeLogin());
    }
  }, [secureSignOut, urlHistory]);

  const checkSession = async () => {
    if (urlLocation.pathname === routeLogin()) {
      setIsCheckingSession(false);
      return;
    }

    setIsCheckingSession(true);

    try {
      const session = await getCurrentSession();
      
      if (!session) {
        console.log('No hay sesión - redirigiendo a login');
        urlHistory.replace(routeLogin());
        setIsCheckingSession(false);
        return;
      }

      const aalData = await getAuthenticatorAssuranceLevel();
      const currentAAL = aalData?.currentLevel;
      
      
      if (!currentAAL || currentAAL !== 'aal2') {
        console.log('Redirigiendo a login para completar autenticación MFA...');
        
        await secureSignOut();
        deleteTokenCookie();
        sessionStorage.removeItem('user_id');
        
        urlHistory.replace(routeLogin());
        setIsCheckingSession(false);
        return;
      }

      console.log('Sesión válida con MFA completo (AAL2)');
      setIsCheckingSession(false);
    } catch (error) {
      console.error('Error verificando sesión:', error);
      urlHistory.replace(routeLogin());
      setIsCheckingSession(false);
    }
  }

  useEffect(() => {
   checkSession();
  }, [urlLocation.pathname]);


  return (
    <SessionManageContext.Provider value={{ login, submitLogin, submitLogout }}>
      {isCheckingSession && urlLocation.pathname !== routeLogin() ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px'
        }}>
          Verificando sesión...
        </div>
      ) : (
        <SelectLayout
          notHeader={urlLocation?.pathname === routeLogin()}
          isSingle={singleLayoutByRoutes.includes(urlLocation.pathname)}
        >
          {children}
        </SelectLayout>
      )}
    </SessionManageContext.Provider>
  );
}

function SelectLayout({ children, isSingle = true, notHeader = false }: ItfSelectLayoutProps) {
  if (isSingle) return <SingleLayout notHeader={notHeader}>{children}</SingleLayout>;
  return <GeneralLayout>{children}</GeneralLayout>;
}

interface ItfAuthManageContext {
  login: any;
  submitLogin: (body: LoginParams) => void;
  submitLogout: () => void;
}

interface ItfSelectLayoutProps {
  children: ReactNode;
  isSingle?: boolean;
  notHeader?: boolean;
}
